import { useEffect, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import Youtube from "@tiptap/extension-youtube";
import { http } from "@/lib/api";
import { toast } from "sonner";
import PhotoLibraryPicker from "@/components/PhotoLibraryPicker";
import {
  TextB, TextItalic, TextUnderline, TextStrikethrough,
  TextAlignLeft, TextAlignCenter, TextAlignRight,
  ListBullets, ListNumbers, Quotes, Link as LinkI, Image as ImageI,
  ArrowUUpLeft, ArrowUUpRight, Code, TextH, YoutubeLogo, Microphone,
  Images,
} from "@phosphor-icons/react";

/**
 * RichEditor — a TipTap-based Blogger-style editor.
 *
 * Extensions in play:
 *   • StarterKit          → headings, paragraphs, bold, italic, code,
 *                           bullet + ordered lists, blockquote, hr, undo/redo
 *   • Underline           → underline mark (missing from StarterKit)
 *   • Link                → inline anchor tags with sanitised targets
 *   • Image               → inline images (drag/paste too via TipTap)
 *   • Placeholder         → the empty-state hint prompt
 *   • TextAlign           → left / center / right alignment for paragraphs
 *
 * Multi-language input piggy-backs on the browser IME + a companion
 * `TransliterateInput` when the writer picks a Manglish-style language —
 * TipTap itself doesn't need any special config for CJK / Indic input.
 */

const ToolbarButton = ({ onClick, active, disabled, title, testid, children }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    disabled={disabled}
    data-testid={testid}
    title={title}
    className={
      "inline-flex items-center justify-center h-8 min-w-8 px-2 rounded-md text-[13px] " +
      (active
        ? "bg-[color:var(--np-brand,#B85F2A)] text-white "
        : "bg-white text-[#241209] hover:bg-black/5 ") +
      "disabled:opacity-40 border border-black/10"
    }
  >
    {children}
  </button>
);

export default function RichEditor({
  value,
  onChange,
  placeholder = "Write your article…",
  testid = "rich-editor",
}) {
  const [libOpen, setLibOpen] = useState(false);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3, 4] } }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          rel: "noreferrer noopener",
          target: "_blank",
          class: "text-[color:var(--np-brand,#B85F2A)] underline",
        },
      }),
      Image.configure({ inline: false, allowBase64: false, HTMLAttributes: { class: "rounded-lg" } }),
      Youtube.configure({ nocookie: true, controls: true, HTMLAttributes: { class: "cms-youtube rounded-lg" } }),
      Placeholder.configure({ placeholder }),
      TextAlign.configure({ types: ["heading", "paragraph", "image"] }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class:
          "prose prose-sm md:prose-base max-w-none min-h-[300px] p-4 focus:outline-none " +
          "prose-headings:font-serif-display prose-a:text-[color:var(--np-brand,#B85F2A)]",
        "data-testid": `${testid}-content`,
      },
    },
    onUpdate: ({ editor: ed }) => onChange?.(ed.getHTML()),
  });

  // Keep external `value` in sync when the parent resets (e.g. loading a
  // draft into the editor after nav). Skip the update if the incoming HTML
  // matches — avoids caret jumps mid-typing.
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value !== undefined && value !== null && value !== current) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return <div className="min-h-[300px] bg-white/60 animate-pulse rounded-lg" />;

  const addLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL (podcast, article, PDF, anything — leave blank to remove):", prev);
    if (url === null) return;
    if (url === "") { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };
  const addImage = () => {
    // File upload path — real object-storage flow. Falls back to URL prompt.
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      if (file.size > 20 * 1024 * 1024) { toast.error("Image too large (max 20 MB)"); return; }
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const { data } = await http.post("/uploads/photo", {
            image_base64: reader.result,
            content_type: file.type,
          });
          editor.chain().focus().setImage({ src: data.url, alt: file.name }).run();
          toast.success("Image inserted");
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Upload failed");
        }
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };
  const addImageByUrl = () => {
    const url = window.prompt("Image URL (or hit Cancel and use the Upload button):");
    if (!url) return;
    editor.chain().focus().setImage({ src: url, alt: "" }).run();
  };
  const addYoutube = () => {
    const url = window.prompt("YouTube URL (video, shorts, or youtu.be):");
    if (!url) return;
    editor.commands.setYoutubeVideo({ src: url });
  };
  const addPodcast = () => {
    const url = window.prompt("Podcast / audio URL (mp3, m4a, Spotify, Anchor…):");
    if (!url) return;
    const isDirect = /\.(mp3|m4a|wav|ogg)(\?|$)/i.test(url);
    const html = isDirect
      ? `<p><audio controls preload="none" src="${url}" style="width:100%"></audio></p>`
      : `<p><a href="${url}" class="cms-podcast-link" target="_blank" rel="noreferrer">🎧 Listen to podcast</a></p>`;
    editor.chain().focus().insertContent(html).run();
  };

  return (
    <div className="rounded-lg border border-black/15 bg-white overflow-hidden" data-testid={testid}>
      {/* Toolbar */}
      <div className="flex flex-wrap gap-1 p-2 border-b border-black/10 bg-[#FBF2E1]/70" data-testid={`${testid}-toolbar`}>
        <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold" testid={`${testid}-bold`}><TextB size={14} weight="bold" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic" testid={`${testid}-italic`}><TextItalic size={14} weight="bold" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title="Underline" testid={`${testid}-underline`}><TextUnderline size={14} weight="bold" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} title="Strikethrough" testid={`${testid}-strike`}><TextStrikethrough size={14} weight="bold" /></ToolbarButton>
        <span className="w-px bg-black/10 mx-1 self-stretch" />
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="H2" testid={`${testid}-h2`}><TextH size={14} weight="bold" /><sub className="text-[9px]">2</sub></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="H3" testid={`${testid}-h3`}><TextH size={14} weight="bold" /><sub className="text-[9px]">3</sub></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()} active={editor.isActive("heading", { level: 4 })} title="H4" testid={`${testid}-h4`}><TextH size={14} weight="bold" /><sub className="text-[9px]">4</sub></ToolbarButton>
        <span className="w-px bg-black/10 mx-1 self-stretch" />
        <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bulleted list" testid={`${testid}-ul`}><ListBullets size={14} weight="bold" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numbered list" testid={`${testid}-ol`}><ListNumbers size={14} weight="bold" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Quote" testid={`${testid}-quote`}><Quotes size={14} weight="bold" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive("codeBlock")} title="Code block" testid={`${testid}-code`}><Code size={14} weight="bold" /></ToolbarButton>
        <span className="w-px bg-black/10 mx-1 self-stretch" />
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })} title="Align left" testid={`${testid}-align-left`}><TextAlignLeft size={14} weight="bold" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} title="Align center" testid={`${testid}-align-center`}><TextAlignCenter size={14} weight="bold" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign("right").run()} active={editor.isActive({ textAlign: "right" })} title="Align right" testid={`${testid}-align-right`}><TextAlignRight size={14} weight="bold" /></ToolbarButton>
        <span className="w-px bg-black/10 mx-1 self-stretch" />
        <ToolbarButton onClick={addLink} active={editor.isActive("link")} title="Link (any URL — podcast, PDF, article)" testid={`${testid}-link`}><LinkI size={14} weight="bold" /></ToolbarButton>
        <ToolbarButton onClick={addImage} title="Upload image" testid={`${testid}-image`}><ImageI size={14} weight="bold" /></ToolbarButton>
        <ToolbarButton onClick={() => setLibOpen(true)} title="Insert from Photo Library" testid={`${testid}-library`}><Images size={14} weight="bold" /></ToolbarButton>
        <ToolbarButton onClick={addImageByUrl} title="Insert image by URL" testid={`${testid}-image-url`}><ImageI size={14} weight="light" /></ToolbarButton>
        <ToolbarButton onClick={addYoutube} title="Embed YouTube video" testid={`${testid}-youtube`}><YoutubeLogo size={14} weight="bold" /></ToolbarButton>
        <ToolbarButton onClick={addPodcast} title="Insert podcast / audio link" testid={`${testid}-podcast`}><Microphone size={14} weight="bold" /></ToolbarButton>
        <span className="w-px bg-black/10 mx-1 self-stretch" />
        <ToolbarButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo" testid={`${testid}-undo`}><ArrowUUpLeft size={14} weight="bold" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo" testid={`${testid}-redo`}><ArrowUUpRight size={14} weight="bold" /></ToolbarButton>
      </div>
      <EditorContent editor={editor} />
      <PhotoLibraryPicker
        open={libOpen}
        onClose={() => setLibOpen(false)}
        onSelect={({ url, alt }) => {
          if (url) editor.chain().focus().setImage({ src: url, alt: alt || "" }).run();
        }}
      />
    </div>
  );
}
